import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateInvitation } from '../hooks/use-invitations'
import { useOrganizationTeams } from '../hooks/use-organization-teams'
import { cn } from '@/shared/lib/utils'
import type { UserRole } from '@/shared/types'

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function InviteUserModal({ isOpen, onClose }: InviteUserModalProps) {
  const { t } = useTranslation()
  const createInvitation = useCreateInvitation()
  const { data: teams = [] } = useOrganizationTeams()

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<UserRole>('user')
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate email
    if (!email.trim()) {
      setError(t('settings.team.invite.emailRequired'))
      return
    }

    if (!EMAIL_REGEX.test(email)) {
      setError(t('settings.team.invite.emailInvalid'))
      return
    }

    try {
      await createInvitation.mutateAsync({
        email: email.trim().toLowerCase(),
        role,
        teamIds: selectedTeamIds,
      })

      // Reset form and close modal
      setEmail('')
      setRole('user')
      setSelectedTeamIds([])
      onClose()
    } catch (err) {
      if (err instanceof Error) {
        if (err.message === 'USER_EXISTS') {
          setError(t('settings.team.invite.userExists'))
        } else {
          setError(err.message)
        }
      }
    }
  }

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    )
  }

  const handleClose = () => {
    setEmail('')
    setRole('user')
    setSelectedTeamIds([])
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            {t('settings.team.invite.title')}
          </h2>
          <p className="mt-1 text-sm text-foreground-muted">
            {t('settings.team.invite.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('settings.team.invite.emailLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('settings.team.invite.emailPlaceholder')}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-foreground-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('settings.team.invite.roleLabel')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['admin', 'user', 'viewer'] as UserRole[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                    role === r
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border bg-background text-foreground hover:border-accent/50'
                  )}
                >
                  {t(`settings.team.roles.${r}`)}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-foreground-muted">
              {t(`settings.team.invite.roleHint.${role}`)}
            </p>
          </div>

          {/* Teams Selection */}
          {teams.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('settings.team.invite.teamsLabel')}
              </label>
              <div className="max-h-32 overflow-y-auto rounded-lg border border-border bg-background p-2 space-y-1">
                {teams.map((team) => (
                  <label
                    key={team.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-background-secondary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTeamIds.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                      className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm text-foreground">{team.name}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-foreground-muted">
                {t('settings.team.invite.teamsHint')}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-background-secondary transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createInvitation.isPending}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors disabled:opacity-50"
            >
              {createInvitation.isPending
                ? t('settings.team.invite.sending')
                : t('settings.team.invite.sendInvitation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
